/**
 * Collapsible CCG (PRODUCT_SET) card, the root of the ADSP entity tree. The first
 * CCG opens by default; the rest start collapsed and lazy-load their CONCEPTs on
 * expand. Concepts join by CONCEPT.parentSourceEntityId === PRODUCT_SET.publisherEntityId
 * (the CCG's publisher-side id, not its sourceEntityId).
 *
 * This is the only level the Ads Manager status filter narrows: the CCG above is
 * always listed, and a matching concept always shows its full creative/asset body.
 */
import React, { useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import MeridianLoader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import { useTranslation } from 'src/arb-wrapper';
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import { AdspConceptBlock } from './AdspConceptBlock';
import { AdspPaginationFooter } from './AdspPaginationFooter';
import { EntityIcon } from './EntityIcon';
import { readCcgName, resolveEmptyStateKey } from './adspEntityHelpers';
import { EMPTY_VALUE, StatusBadge } from './StatusBadge';
import type { AdsStatusFilter } from './statusFilterParams';
import { useAdspEntityLevel } from './useAdspEntityLevel';

interface AdspProductSetCardProps {
  campaignId: string;
  defaultExpanded: boolean;
  productSet: PublisherEntityDto;
  statusFilter: AdsStatusFilter;
}

export const AdspProductSetCard: React.FC<AdspProductSetCardProps> = ({
  campaignId,
  defaultExpanded,
  productSet,
  statusFilter,
}) => {
  const { t } = useTranslation();
  const [hasExpanded, setHasExpanded] = useState(defaultExpanded);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const name = readCcgName(productSet) ?? productSet.publisherEntityId ?? productSet.sourceEntityId ?? EMPTY_VALUE;

  // The CCG's publisherEntityId is an ACP id (URL-safe), so it is safe to sign as a query value;
  // the levels below use row `_id`s instead because their source ids embed free-text titles.
  const { page, query, setPage } = useAdspEntityLevel({
    campaignId,
    parentSourceEntityId: productSet.publisherEntityId,
    publisherEntityType: 'CONCEPT',
    skip: !hasExpanded || productSet.publisherEntityId == null,
    statusFilter,
  });

  const concepts = (query.data?.data ?? []) as PublisherEntityDto[];
  const pagination = query.data?.meta.pagination;
  const conceptTotal = pagination?.total;
  const emptyStateKey = resolveEmptyStateKey(statusFilter, 'ads_manager_no_ccg_concepts');

  const handleToggle = (): void => {
    if (!hasExpanded) setHasExpanded(true);
    setIsExpanded(prev => !prev);
  };

  return (
    <div style={{ borderRadius: '8px', overflow: 'hidden' }}>
      <Box backgroundColor="subdued" data-testid={`ads-manager-product-set-${productSet._id}`} spacingInset="400">
        <Column spacing="400">
          <div
            style={{ alignItems: 'center', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
            onClick={handleToggle}
          >
            <Row alignmentVertical="center" spacing="300">
              <Icon tokens={isExpanded ? chevronDownSmallTokens : chevronRightSmallTokens} />
              <EntityIcon size={32} type={productSet.publisherEntityType} />
              <Column spacing="none">
                <Text type="b300">{name}</Text>
                <Text color="secondary" type="b100">{t('ads_manager_ccg_id_label')}: {productSet.publisherEntityId ?? EMPTY_VALUE}</Text>
              </Column>
              {conceptTotal != null && conceptTotal > 0 && (
                <Text color="secondary" type="b100">
                  {t('ads_manager_concepts_label')} ({conceptTotal})
                </Text>
              )}
            </Row>
            <StatusBadge entity={productSet} />
          </div>
          {productSet.lastError != null && (
            <Text color="error" type="b200">{productSet.lastError}</Text>
          )}

          {isExpanded && (
            query.isError
              ? (
                <Alert
                  data-testid={`ads-manager-product-set-concepts-error-${productSet._id}`}
                  size="small"
                  type="error"
                >
                  {t('ads_manager_query_error')}
                </Alert>
                )
              : query.isLoading
                ? (
                  <div data-testid={`ads-manager-product-set-concepts-loading-${productSet._id}`}>
                    <MeridianLoader size="small" type="linear" />
                  </div>
                  )
                : concepts.length === 0
                  ? (
                  <Text
                    color="secondary"
                    data-testid={`ads-manager-product-set-no-concepts-${productSet._id}`}
                  >
                    {t(emptyStateKey)}
                  </Text>
                    )
                  : (
                  <Column data-testid={`ads-manager-product-set-concepts-${productSet._id}`} spacing="300">
                    {concepts.map(concept => (
                      <AdspConceptBlock
                        campaignId={campaignId}
                        concept={concept}
                        key={concept._id}
                      />
                    ))}
                  </Column>
                    )
          )}
          {isExpanded && pagination != null && (
            <AdspPaginationFooter
              isFetching={query.isFetching}
              page={page}
              pagination={pagination}
              testId={`ads-manager-product-set-concepts-${productSet._id}`}
              onPageChange={setPage}
            />
          )}
        </Column>
      </Box>
    </div>
  );
};
